/*
 * The library's folders as pure functions: the tree (on the Case Files tree
 * helpers, which a folder fits as a `TreeNodeLike`), paths, live counts, the
 * rows the rail shows, what a move may do and what an upload will create.
 * One folder per document; tags and visas cover the other axes. No React or
 * IO here so the rules stay unit-testable.
 */
import {
  buildTree,
  canDropInto,
  childrenOf,
  type FsTree,
  type TreeNodeLike
} from "../caseFiles/navigation";
import type { FirmLibraryEntry, FirmLibraryFolder } from "./types";

/** The server's limit: a top-level folder is depth 1. */
export const MAX_FOLDER_DEPTH = 4;

/** The rail's two fixed rows; anything else is a folder id. */
export const ALL_DOCUMENTS = "all";
export const UNFILED = "unfiled";

export type FolderNode = FirmLibraryFolder & TreeNodeLike;
export type FolderTree = FsTree<FolderNode>;

export const folderTree = (folders: FirmLibraryFolder[]): FolderTree =>
  buildTree(folders.map((f): FolderNode => ({ ...f, kind: "folder" })));

export const childFolders = (
  tree: FolderTree,
  parentId: string | null
): FolderNode[] => childrenOf(tree, parentId);

export const isFolderScope = (scope: string): boolean =>
  scope !== ALL_DOCUMENTS && scope !== UNFILED;

/** The folder and its ancestors, top level first; empty for an unknown id. */
export const folderChain = (
  tree: FolderTree,
  folderId: string | null | undefined
): FolderNode[] => {
  const chain: FolderNode[] = [];
  const seen = new Set<string>();
  let cursor = folderId ? tree.byId.get(folderId) : undefined;
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    chain.unshift(cursor);
    cursor = cursor.parent_id ? tree.byId.get(cursor.parent_id) : undefined;
  }
  return chain;
};

/** "Companies / Harbor Robotics LLC", or "Unfiled". */
export const folderPathLabel = (
  tree: FolderTree,
  folderId: string | null | undefined
): string => {
  const chain = folderChain(tree, folderId);
  return chain.length ? chain.map((f) => f.name).join(" / ") : "Unfiled";
};

/** How deep a folder sits (top level is 1; `null`, the top, is 0). */
export const depthOf = (tree: FolderTree, folderId: string | null): number =>
  folderChain(tree, folderId).length;

/** The folder and every folder under it. */
export const descendantIds = (tree: FolderTree, folderId: string): string[] => {
  const out: string[] = [];
  const stack = [folderId];
  while (stack.length) {
    const current = stack.pop() as string;
    if (!out.includes(current)) {
      out.push(current);
      stack.push(...childFolders(tree, current).map((f) => f.id));
    }
  }
  return out;
};

/** Levels in the subtree under a folder (no subfolders: 1). */
const heightOf = (tree: FolderTree, folderId: string): number => {
  const walk = (id: string, seen: Set<string>): number => {
    const kids = childFolders(tree, id).filter((f) => !seen.has(f.id));
    return (
      1 + Math.max(0, ...kids.map((k) => walk(k.id, new Set([...seen, k.id]))))
    );
  };
  return walk(folderId, new Set([folderId]));
};

/** A new folder fits under `parentId` (null: the top level). */
export const canAddSubfolder = (
  tree: FolderTree,
  parentId: string | null
): boolean => depthOf(tree, parentId) < MAX_FOLDER_DEPTH;

/**
 * Whether a folder may move under `targetId` (null: the top level): never
 * into itself or a descendant, and never so deep the tree passes four levels.
 */
export const canMoveFolder = (
  tree: FolderTree,
  folderId: string,
  targetId: string | null
): boolean =>
  canDropInto(tree, [folderId], targetId) &&
  depthOf(tree, targetId) + heightOf(tree, folderId) <= MAX_FOLDER_DEPTH;

/** Documents filed in each folder, its subfolders' included. */
export const folderCounts = (
  tree: FolderTree,
  entries: FirmLibraryEntry[]
): Map<string, number> => {
  const direct = new Map<string, number>();
  for (const entry of entries) {
    if (entry.folder_id && tree.byId.has(entry.folder_id))
      direct.set(entry.folder_id, (direct.get(entry.folder_id) ?? 0) + 1);
  }
  const totals = new Map<string, number>();
  const total = (id: string, seen: Set<string>): number => {
    const cached = totals.get(id);
    if (cached !== undefined) return cached;
    const sum =
      (direct.get(id) ?? 0) +
      childFolders(tree, id)
        .filter((f) => !seen.has(f.id))
        .reduce((n, f) => n + total(f.id, new Set([...seen, f.id])), 0);
    totals.set(id, sum);
    return sum;
  };
  tree.byId.forEach((_, id) => total(id, new Set([id])));
  return totals;
};

/** An entry is Unfiled when it has no folder or its folder is gone. */
export const isUnfiled = (tree: FolderTree, entry: FirmLibraryEntry) =>
  !entry.folder_id || !tree.byId.has(entry.folder_id);

/** The rows a scope lists directly (a folder: its own documents only). */
export const entriesInScope = (
  tree: FolderTree,
  entries: FirmLibraryEntry[],
  scope: string
): FirmLibraryEntry[] => {
  if (scope === ALL_DOCUMENTS) return entries;
  if (scope === UNFILED) return entries.filter((e) => isUnfiled(tree, e));
  return entries.filter((e) => e.folder_id === scope);
};

/** A folder's documents and its subfolders' (search narrowed by In:). */
export const entriesUnder = (
  tree: FolderTree,
  entries: FirmLibraryEntry[],
  folderId: string
): FirmLibraryEntry[] => {
  const ids = new Set(descendantIds(tree, folderId));
  return entries.filter((e) => e.folder_id && ids.has(e.folder_id));
};

export type FolderRow = {
  folder: FolderNode;
  depth: number;
  hasChildren: boolean;
};

/** The rail's rows, depth first; children only under expanded folders. */
export const folderRows = (
  tree: FolderTree,
  expanded: ReadonlySet<string> | "all",
  parentId: string | null = null,
  depth = 0
): FolderRow[] =>
  childFolders(tree, parentId).flatMap((folder) => {
    const kids = childFolders(tree, folder.id);
    const open = expanded === "all" || expanded.has(folder.id);
    return [
      { folder, depth, hasChildren: kids.length > 0 },
      ...(open ? folderRows(tree, expanded, folder.id, depth + 1) : [])
    ];
  });

/** Where an agent reads the entry: `/firm/library/<folder path>/<name>.<ext>`. */
export const agentLibraryPath = (
  tree: FolderTree,
  entry: Pick<
    FirmLibraryEntry,
    "folder_id" | "name" | "id" | "original_filename" | "is_pdf"
  >
): string => {
  const clean = (s: string) =>
    s
      .replace(/[\\/:*?"<>|]+/g, "-")
      .trim()
      .replace(/^[. ]+|[. ]+$/g, "");
  const original = (entry.original_filename ?? "").split("/").pop() ?? "";
  const ext = original.includes(".")
    ? (original.split(".").pop() as string).toLowerCase()
    : entry.is_pdf
    ? "pdf"
    : "docx";
  const folders = folderChain(tree, entry.folder_id).map(
    (f) => clean(f.name) || "folder"
  );
  const name = clean(entry.name || entry.id) || entry.id;
  return ["/firm/library", ...folders, `${name}.${ext}`].join("/");
};

/** The folders of a dropped file's path: `"A/B/c.pdf"` -> `["A", "B"]`. */
export const folderPartsOf = (relativePath: string | null | undefined) =>
  String(relativePath ?? "")
    .split(/[\\/]/)
    .slice(0, -1)
    .map((p) => p.trim())
    .filter((p) => p && p !== "." && p !== "..");

const nameKey = (name: string) =>
  name.trim().replace(/\s+/g, " ").toLocaleLowerCase();

export type UploadPlan = {
  /** Distinct folder paths in the drop, as they will be after merging. */
  folders: string[][];
  /** Top-level dropped folders that already exist at the destination. */
  merged: string[];
  /** Folders that will be created. */
  created: number;
  /** Some path runs past four levels and flattens into the fourth. */
  flattened: boolean;
};

/**
 * What an upload into `destinationId` will create, mirroring the server's
 * merge-by-name and four-level cap.
 */
export const planUpload = (
  tree: FolderTree,
  destinationId: string | null,
  relativePaths: string[]
): UploadPlan => {
  const room = MAX_FOLDER_DEPTH - depthOf(tree, destinationId);
  const seen = new Map<string, string[]>();
  let flattened = false;
  for (const path of relativePaths) {
    const parts = folderPartsOf(path);
    if (parts.length > room) flattened = true;
    const kept = parts.slice(0, Math.max(0, room));
    for (let i = 1; i <= kept.length; i += 1) {
      const prefix = kept.slice(0, i);
      const key = prefix.map(nameKey).join("/");
      if (!seen.has(key)) seen.set(key, prefix);
    }
  }
  const folders = [...seen.values()];
  const existing = (path: string[]): FolderNode | null => {
    let parent: string | null = destinationId;
    let match: FolderNode | undefined;
    for (const name of path) {
      match = childFolders(tree, parent).find(
        (f) => nameKey(f.name) === nameKey(name)
      );
      if (!match) return null;
      parent = match.id;
    }
    return match ?? null;
  };
  return {
    folders,
    merged: folders
      .filter((p) => p.length === 1)
      .map((p) => existing(p)?.name)
      .filter((name): name is string => Boolean(name)),
    created: folders.filter((p) => !existing(p)).length,
    flattened
  };
};
